import type { SmallGroupMember } from "@/types";

/**
 * What a roster row may offer, in the order it offers it.
 *
 * A plain module rather than a helper inside the screen, and that is what makes
 * it testable at all: the root suite runs `.ts` through Node's type stripping,
 * which cannot parse JSX, so anything living in `group/[id].tsx` can only ever
 * be checked by regex over its own source. Which actions a row offers is the
 * part worth pinning properly — it is a rule about who may reach whom — so it
 * lives where a test can call it.
 */
export type MemberActionKind = "message" | "cue" | "report" | "remove";

export function memberActionsFor(input: {
  member: Pick<SmallGroupMember, "allowMemberCues" | "chatId" | "isHost">;
  isViewer: boolean;
  viewerIsHost: boolean;
}): MemberActionKind[] {
  // Nothing to do to yourself. The viewer's own decision — whether this group
  // may cue them — is a switch above the list, not an action on their own row:
  // it is a statement about everybody here, not about one person.
  if (input.isViewer) return [];

  const actions: MemberActionKind[] = [];

  if (input.member.chatId) {
    // Message REPLACES Cue rather than joining it. Two people who already talk
    // do not need to ask again, and the send path would refuse the Cue anyway
    // on conversation history — so offering both would be offering one button
    // that works and one that apologises.
    actions.push("message");
  } else if (input.member.allowMemberCues) {
    actions.push("cue");
  }
  // Otherwise: no Cue row at all, rather than a permanently disabled one.
  //
  // This is the trade behind the collapse in the roster, and it is worth
  // stating. A greyed row saying "not accepting Cues here" is informative the
  // first time and dead weight every time after — and early in a group's life,
  // before anybody has opted in, it is dead weight on EVERY row. Hiding it is
  // what makes the single-Report case real, which is what makes collapsing the
  // menu worth having at all. The cost is discoverability: somebody who never
  // opts in themselves may not learn the feature exists from this screen alone.
  // The join sheet and the switch above the roster are where it is taught.

  actions.push("report");

  // Host only, and never on the host's own row — a host cannot remove
  // themselves, they leave.
  if (input.viewerIsHost && !input.member.isHost) actions.push("remove");

  return actions;
}
